import { Position } from "geojson";

import { ValidationFunction } from "./GenericValidator";

interface MakeValidateUgPathOptions {
  /** Layer ID of the ugPoints layer — both endpoints of a ugPath must be colocated with a feature in this layer. */
  ugPointsLayerId: string;
  /** Optional layer ID of the aerial poles layer. When provided, an aerial pole is also a valid endpoint. */
  aerialPoleLayerId?: string;
}

/**
 * Factory for a ugPath validator.
 *
 * A ugPath is valid if:
 * - The first vertex is colocated with a ugPoint or aerial pole
 * - The last vertex is colocated with a ugPoint or aerial pole
 */
export const makeValidateUgPath =
  ({ ugPointsLayerId, aerialPoleLayerId }: MakeValidateUgPathOptions): ValidationFunction =>
  (feature, _allFeatures, helpers) => {
    const featureId = feature.id as string;
    const errors: string[] = [];

    if (feature.geometry.type !== "LineString") {
      return {
        featureId,
        valid: false,
        errors: ["ugPath must be a LineString feature"],
      };
    }

    const coords = feature.geometry.coordinates;

    if (coords.length < 2) {
      errors.push("ugPath must have at least 2 vertices");
      return {
        featureId,
        valid: false,
        errors,
      };
    }

    const firstVertex = coords[0];
    const lastVertex = coords[coords.length - 1];

    // Check if both endpoints are colocated with a ugPoint or aerial pole
    const firstAtUgPoint = helpers.findFeaturesAtPosition(firstVertex, ugPointsLayerId).length > 0;
    const firstAtAerialPole = aerialPoleLayerId ? helpers.findFeaturesAtPosition(firstVertex, aerialPoleLayerId).length > 0 : false;
    const lastAtUgPoint = helpers.findFeaturesAtPosition(lastVertex, ugPointsLayerId).length > 0;
    const lastAtAerialPole = aerialPoleLayerId ? helpers.findFeaturesAtPosition(lastVertex, aerialPoleLayerId).length > 0 : false;

    const networkPointLabel = aerialPoleLayerId ? "ugPoint or aerial pole" : "ugPoint";
    const invalidEndpoints: Position[] = [];

    if (!firstAtUgPoint && !firstAtAerialPole) {
      errors.push(`ugPath first vertex must be connected to a ${networkPointLabel}`);
      invalidEndpoints.push(firstVertex);
    }
    if (!lastAtUgPoint && !lastAtAerialPole) {
      errors.push(`ugPath last vertex must be connected to a ${networkPointLabel}`);
      invalidEndpoints.push(lastVertex);
    }

    return {
      featureId,
      valid: errors.length === 0,
      errors,
      ...(invalidEndpoints.length > 0 && { invalidEndpoints }),
    };
  };

interface MakeValidateUgPointOptions {
  /** Layer ID of the ugPath layer. */
  ugPathLayerId: string;
  /** Optional layer ID of the leadins layer. When provided, leadin endpoints also count as connections. */
  leadinsLayerId?: string;
}

/**
 * Factory for a ugPoint validator.
 *
 * A ugPoint is valid if it has at least one ugPath connected to it (or,
 * when `leadinsLayerId` is provided, at least one connected leadIn endpoint).
 */
export const makeValidateUgPoint =
  ({ ugPathLayerId, leadinsLayerId }: MakeValidateUgPointOptions): ValidationFunction =>
  (feature, _allFeatures, helpers) => {
    const featureId = feature.id as string;
    const errors: string[] = [];

    if (feature.geometry.type !== "Point") {
      return {
        featureId,
        valid: false,
        errors: ["ugPoint must be a Point feature"],
      };
    }

    const ugPointPosition = feature.geometry.coordinates;

    // Find all leadIns and ugPaths colocated at this ugPoint
    const colocatedUgPaths = helpers.findFeaturesAtPosition(ugPointPosition, ugPathLayerId);

    // Check if any leadIn has EITHER end at this ugPoint
    let connectedLeadInCount = 0;
    if (leadinsLayerId) {
      const colocatedLeadIns = helpers.findFeaturesAtPosition(ugPointPosition, leadinsLayerId);
      connectedLeadInCount = colocatedLeadIns.filter((leadIn) => {
        if (leadIn.geometry.type !== "LineString") return false;

        const coords = leadIn.geometry.coordinates;
        const firstVertex = coords[0];
        const lastVertex = coords[coords.length - 1];

        const firstColocated = helpers.isColocated(ugPointPosition, firstVertex);
        const lastColocated = helpers.isColocated(ugPointPosition, lastVertex);

        // Either end can be connected to the ugPoint
        return firstColocated || lastColocated;
      }).length;
    }

    // Check if any ugPath has any vertex at this ugPoint
    const connectedUgPaths = colocatedUgPaths.filter((ugPath) => {
      if (ugPath.geometry.type !== "LineString") return false;

      return ugPath.geometry.coordinates.some((vertex) => helpers.isColocated(ugPointPosition, vertex));
    });

    const totalConnections = connectedLeadInCount + connectedUgPaths.length;

    if (totalConnections === 0) {
      errors.push(
        leadinsLayerId ? "ugPoint must have at least one leadIn or ugPath connected to it" : "ugPoint must have at least one ugPath connected to it"
      );
    }

    return {
      featureId,
      valid: errors.length === 0,
      errors,
    };
  };

interface MakeValidateDemandOptions {
  /** Layer ID of the leadins layer */
  leadinsLayerId: string;
}

/**
 * Factory for a demand-point validator.
 *
 * A demand point is valid if:
 * - It has at least one leadIn (LineString) connected to it
 *
 * When a demand point is moved, this validation will be triggered for:
 * - The demand point itself
 * - Any leadIn features that were connected before the move
 * - Any leadIn features that are connected after the move
 */
export const makeValidateDemand =
  ({ leadinsLayerId }: MakeValidateDemandOptions): ValidationFunction =>
  (feature, _allFeatures, helpers) => {
    const featureId = feature.id as string;
    const errors: string[] = [];

    // Get the demand point's position
    if (feature.geometry.type !== "Point") {
      return {
        featureId,
        valid: false,
        errors: ["Demand must be a Point feature"],
      };
    }

    const demandPosition = feature.geometry.coordinates;

    // Find all leadIn features colocated at this demand point
    const colocatedLeadIns = helpers.findFeaturesAtPosition(demandPosition, leadinsLayerId);

    // Check if any leadIn has EITHER end at this demand point
    const connectedLeadIns = colocatedLeadIns.filter((leadIn) => {
      if (leadIn.geometry.type !== "LineString") return false;

      const coords = leadIn.geometry.coordinates;
      const firstVertex = coords[0];
      const lastVertex = coords[coords.length - 1];

      const firstColocated = helpers.isColocated(demandPosition, firstVertex);
      const lastColocated = helpers.isColocated(demandPosition, lastVertex);

      // Either end can be connected to the demand
      return firstColocated || lastColocated;
    });

    if (connectedLeadIns.length === 0) {
      errors.push("Demand point must have at least one leadIn connected to it");
    }

    return {
      featureId,
      valid: errors.length === 0,
      errors,
    };
  };

interface MakeValidateAerialPoleOptions {
  /** Layer ID of the aerial span layer. */
  spanLayerId: string;
}

/**
 * Factory for an aerial pole validator.
 *
 * A pole is valid if it has at least one aerial span connected to it
 * (i.e. a span vertex is colocated with the pole).
 */
export const makeValidateAerialPole =
  ({ spanLayerId }: MakeValidateAerialPoleOptions): ValidationFunction =>
  (feature, _allFeatures, helpers) => {
    const featureId = feature.id as string;
    const errors: string[] = [];

    if (feature.geometry.type !== "Point") {
      return {
        featureId,
        valid: false,
        errors: ["Pole must be a Point feature"],
      };
    }

    const polePosition = feature.geometry.coordinates;

    const colocatedSpans = helpers.findFeaturesAtPosition(polePosition, spanLayerId);

    const connectedSpans = colocatedSpans.filter((span) => {
      if (span.geometry.type !== "LineString") return false;
      return span.geometry.coordinates.some((vertex) => helpers.isColocated(polePosition, vertex));
    });

    if (connectedSpans.length === 0) {
      errors.push("Pole must be connected to at least one span");
    }

    return {
      featureId,
      valid: errors.length === 0,
      errors,
    };
  };

interface MakeValidateLeadInOptions {
  /** Layer ID of the "demand-like" source point layer (e.g. ISG demand, MU_POWER addresses). */
  demandLayerId: string;
  /** Layer ID of the ugPoints layer. */
  ugPointsLayerId: string;
  /** Optional layer ID of the aerial poles layer. When provided, an aerial pole is also a valid network endpoint. */
  aerialPoleLayerId?: string;
}

/**
 * Factory for a leadIn validator.
 *
 * A leadIn is valid if:
 * - One end (either vertex) is colocated with a demand-like point
 * - The other end is colocated with a ugPoint or an aerial pole (when aerialPoleLayerId is provided)
 * - The order doesn't matter
 */
export const makeValidateLeadIn =
  ({ demandLayerId, ugPointsLayerId, aerialPoleLayerId }: MakeValidateLeadInOptions): ValidationFunction =>
  (feature, _allFeatures, helpers) => {
    const featureId = feature.id as string;
    const errors: string[] = [];

    if (feature.geometry.type !== "LineString") {
      return {
        featureId,
        valid: false,
        errors: ["LeadIn must be a LineString feature"],
      };
    }

    const coords = feature.geometry.coordinates;

    if (coords.length < 2) {
      errors.push("LeadIn must have at least 2 vertices");
      return {
        featureId,
        valid: false,
        errors,
      };
    }

    const firstVertex = coords[0];
    const lastVertex = coords[coords.length - 1];

    // Check which points are at each end
    const firstAtDemand = helpers.findFeaturesAtPosition(firstVertex, demandLayerId).length > 0;
    const firstAtUgPoint = helpers.findFeaturesAtPosition(firstVertex, ugPointsLayerId).length > 0;
    const firstAtAerialPole = aerialPoleLayerId ? helpers.findFeaturesAtPosition(firstVertex, aerialPoleLayerId).length > 0 : false;

    const lastAtDemand = helpers.findFeaturesAtPosition(lastVertex, demandLayerId).length > 0;
    const lastAtUgPoint = helpers.findFeaturesAtPosition(lastVertex, ugPointsLayerId).length > 0;
    const lastAtAerialPole = aerialPoleLayerId ? helpers.findFeaturesAtPosition(lastVertex, aerialPoleLayerId).length > 0 : false;

    // A network point is a ugPoint or an aerial pole
    const firstAtNetworkPoint = firstAtUgPoint || firstAtAerialPole;
    const lastAtNetworkPoint = lastAtUgPoint || lastAtAerialPole;

    // Valid if one end is at demand and the other is at a network point (order doesn't matter)
    const validConfiguration = (firstAtDemand && lastAtNetworkPoint) || (firstAtNetworkPoint && lastAtDemand);

    if (!validConfiguration) {
      const networkPointLabel = aerialPoleLayerId ? "ugPoint or aerial pole" : "ugPoint";

      if (!firstAtDemand && !lastAtDemand) {
        errors.push("LeadIn must have one end connected to a demand point");
      }
      if (!firstAtNetworkPoint && !lastAtNetworkPoint) {
        errors.push(`LeadIn must have one end connected to a ${networkPointLabel}`);
      }
      if ((firstAtDemand && lastAtDemand) || (firstAtNetworkPoint && lastAtNetworkPoint)) {
        errors.push(`LeadIn must connect a demand point to a ${networkPointLabel} (not two of the same type)`);
      }
    }

    return {
      featureId,
      valid: errors.length === 0,
      errors,
    };
  };
